import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb } from "@fortawesome/free-solid-svg-icons";
import { formatCurrencyAmount } from "../../utils/currency";

const EPS = 0.01;

function sumBilledOnNec(po, orderDetailId) {
    if (!po?.bills?.length || orderDetailId == null) return 0;
    return po.bills
        .filter((b) => b?.order_detail?.id === orderDetailId || b?.order_detail_id === orderDetailId)
        .reduce((acc, b) => acc + Number(b?.total ?? 0), 0);
}

/** Margen NEC: total NEC menos suma de facturas ya ligadas */
function necRemainingMargin(po, od) {
    const t = Number(od?.total ?? 0);
    const used = sumBilledOnNec(po, od.id);
    return t - used;
}

function reasonDisabled(bill, po, od) {
    if (Number(po?.status) !== 0) {
        return "OC no pendiente";
    }
    const c1 = bill?.currency_id ?? bill?.currency?.id;
    const c2 = od?.currency_id ?? od?.currency?.id;
    if (c1 && c2 && Number(c1) !== Number(c2)) {
        return "Moneda distinta";
    }
    const remaining = necRemainingMargin(po, od);
    const bTot = Number(bill?.total || 0);
    if (bTot - remaining > EPS) {
        return "Sin margen en la NEC para este monto";
    }
    return null;
}

export default function AssignNecModal({ show, onHide, bill, poDetail, loading, onConfirm, submitting }) {
    const [selectedId, setSelectedId] = useState(null);

    useEffect(() => {
        if (!show) {
            setSelectedId(null);
        }
    }, [show]);

    const orderDetails = poDetail?.order_details || [];
    const rows = useMemo(() => {
        return orderDetails.map((od) => {
            const dis = reasonDisabled(bill, poDetail, od);
            const billed = sumBilledOnNec(poDetail, od.id);
            const remaining = necRemainingMargin(poDetail, od);
            return { od, billed, remaining, disabled: Boolean(dis), reason: dis };
        });
    }, [bill, poDetail, orderDetails]);

    const canSubmit = selectedId && !submitting;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Seleccionar NEC</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <p className="text-muted mb-0">Cargando datos de la OC…</p>
                ) : !bill || !poDetail ? null : (
                    <>
                        <p className="text-muted small mb-2" id="modal-factura-info">
                            <strong>Factura:</strong> {bill.sn} · {formatCurrencyAmount(bill.currency, bill.total)}
                        </p>
                        <p className="text-muted small mb-3">OC: {poDetail.number || "—"}</p>
                        <div className="alert alert-info d-flex align-items-start gap-2 small mb-3">
                            <FontAwesomeIcon icon={faLightbulb} className="mt-1" />
                            <div>
                                Elija la NEC donde asociar la factura. Se habilitan líneas de la OC
                                pendientes, moneda coincidente y con&nbsp;
                                <strong>margen</strong>
                                suficiente: el total NEC menos la suma de facturas ya ligadas debe cubrir el
                                monto de esta factura (puede haber varias facturas por NEC).
                            </div>
                        </div>
                        <div className="table-responsive">
                            <Table hover size="sm" className="align-middle">
                                <thead>
                                    <tr>
                                        <th style={{ width: 48 }} />
                                        <th>N° NEC</th>
                                        <th>Moneda</th>
                                        <th className="text-end">Monto NEC</th>
                                        <th className="text-end">Ya facturado</th>
                                        <th className="text-end">Disponible</th>
                                        <th>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(({ od, billed, remaining, disabled, reason }) => (
                                        <tr key={od.id} className={!disabled && selectedId === od.id ? "table-primary" : ""}>
                                            <td>
                                                <input
                                                    type="radio"
                                                    name="nec"
                                                    className="form-check-input"
                                                    disabled={disabled}
                                                    checked={selectedId === od.id}
                                                    onChange={() => {
                                                        if (!disabled) {
                                                            setSelectedId(od.id);
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td className="fw-semibold">{od.nec_number || "—"}</td>
                                            <td>{od.currency?.name || "—"}</td>
                                            <td className="text-end">{formatCurrencyAmount(od.currency, od.total)}</td>
                                            <td className="text-end">{formatCurrencyAmount(od.currency, billed)}</td>
                                            <td className="text-end">{formatCurrencyAmount(od.currency, remaining)}</td>
                                            <td>
                                                {disabled ? (
                                                    <span className="text-danger small">{reason}</span>
                                                ) : (
                                                    <span className="text-success small">Puede asignar</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        {orderDetails.length === 0 ? (
                            <p className="text-muted small mb-0">Esta OC no tiene líneas NEC.</p>
                        ) : null}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={submitting}>
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    onClick={() => onConfirm(selectedId)}
                    disabled={!canSubmit}
                >
                    {submitting ? "Asociando…" : "Confirmar asignación"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
